<script setup>
    import Header from './Header.vue'
</script>

<template>
    <Header />

<div class="container mx-auto max-w-prose">
<h1 class="font-bold text-2xl mt-4 mb-2">Terms of Service</h1>

<span class="font-thin">Last modified 27 Februari 2025</span>

<h2>TLDR;</h2>
<ul>
    <li>Do not cheat</li>
    <li>Be nice, even with bad dice!</li>
    <li>Don't do anything bad or break any laws</li>
</ul>

<h2>Use of OpenGammon</h2>
You can use OpenGammon for its intended purpose, playing backgammon (and its variants)
against other people. You can use all parts of the website that are available to
you, and we are free to restrict access to certain parts of the site.

<h2>Creating an Account</h2>
To user OpenGammon you need to be 16 or over, If you're under 16, you can only use
OpenGammon with parental-guardian supervision or consent.

For some features you will need an account. Our goal is to minimize the amount of
personal data that we collect (from you and others). However, to run this service
we need to have some personal data (mostly your email address).

To allow for a competitive platform accounts on OpenGammon are considered personal.
You are not allowed to create multiple accounts or share accounts. Your account should
not impersonate anyone else or have a username that is considered offensive or that violates
anyone's rights. Don't choose a username that inflicts negative emotional reactions. This includes
(but is not limited to the following):
<ul>
    <li>Usernames with offensive/sensitive topics, e.g., race, gender, sexuality, politics.</li>
    <li>Usernames that are disparaging to others.</li>
    <li>Usernames impersonating living people.</li>
    <li>Usernames advertising other websites unrelated to backgammon.</li>
    <li>Usernames implying a BMAB title. If you hold a BMAB title the administration can add a title flair to your account.</li>
</ul>

If you break these rules we withhold the rights to change your username or close 
your account. Moderators will have the final say, and can use their own judgement
in enforcing these rules.

<h2>User Rights</h2>
Both registered and unregistered users of OpenGammon have rights to protect their
property, privacy, and identity unless they have consented or agreed to making their
identity public. Most of the information regarding this can be found in the 
<router-link :to="{name: 'privacy'}">Privacy Policy</router-link>.

By agreeing to these terms users can make use of all the features on OpenGammon for
their own personal, educational, charitable, and/or developmental purposes. OpenGammon
is open-source MIT licensed software. OpenGammon has the ultimate decision in what
kind of use of OpenGammon is appropriate.

Users are free to download or export most of the content they have created for their own
purposes.

Users are responsible for the content that they create and post to OpenGammon. We
are not responsible for the content you post on OpenGammon.

In all circumstances, we may retain certain information as required by law or necessary 
for our legitimate operating purposes. All of the provisions within this agreement 
survive the termination of an account.

<h2>Playing on OpenGammon</h2>
We expect everyone to play to the best of their ability. In case of cheating in any
form we reserve the rights to close the account. 

<h2>Playing on OpenGammon</h2>
All users, both registered as unregistered, on OpenGammon agree to behave with good conduct whilst using OpenGammon's services.
This will always be determined at OpenGammon's discretion. Users who do not behave in
with good conduct may have their account banned or suspended without warning and without
a right to appeal. In all cirumstances we withold the right to ban or close an account on
OpenGammon for any reason without warning, and without having to provide evidence that
the Terms of Service have been breached. 
OpenGammon also reserves the right to remove any content that does not adhere to the
Terms of Service.

<h3>Fair Play</h3>
In case you are not sure how to behave on an online backgammon platform, here are some
examples of what you should not do:
<ol>
    <li><b>Cheating.</b> We define cheating as using external assistance to improve your
        knowledge and/or calculation ability to gain an unfair advantage over your
        opponent. For example using an engine to assist during a game, using (opening) books,
        (online) calculators or asking for move recommendations from a human or software 
        (including human commenters whilst streaming or social media). This is by no means an exhaustive
        list of things we consider cheating, a good rule of thumbis: If you have to
        ask if it is cheating, it probably is.
    </li>
    <li><b>Boosting.</b> This is where a user artificially inflates their rating, by, for example,
        arranging wins in advance with an opponent, or knowingly play a user who is artificially
        deflating their rating, in an attempt to sandbag.
    </li>
    <li><b>Sandbagging.</b> This is where a user artificially deflates their rating by resigning early or
        playing below their level of play.
    </li>
    <li><b>Ragesitting, Ragequitting, and Aborting.</b> This is when a player 
        decides to let the clock run out or leave on purpose, forcing their opponent
        to wait for them, rather than play on or resign. These is all considered
        bad sportmanship, as well as aborting games too frequently.
    </li>
    <li><b>Account sharing.</b> Sharing of accounts is generally not permitted.
    </li>
</ol>

<h3>Communication and Community Guidelines</h3>
We strive to create a fair and helpful environment for all users. In most cases
this comes natural, but sometimes users need a nudge into the right direction. 
We understand that backgammon can sometimes be a frustrating game, but that is no
reason to harrass or otherwise vent your frustrations. Here
are some guidelines to help shaping the community on OpenGammon.

<ol>
    <li><b>Harassing conduct or offensive language.</b> Don't say anything that might
        come off as harassment or offensive. Keep in mind that you are communicating
        with another human. Harassment is a criminal, and will be reported to the
        relevant authorities.
    </li>
    <li><b>Luck talk.</b> We all know it, there is some luck in backgammon, 
        but this is not a reason to emphasise it, or belittle your opponent's 
        feat of winning the game. Excessive 'luck talk' will lead to account 
        suspensions.
    </li>
    <li><b>Rigged dice.</b> Any rigged dice talk on OpenGammon, wether it is about
        OpenGammon or other sites may lead to account suspension. The dice are not
        rigged, the source is available, so if you don't believe it you can check it yourself.
        Even better, the protocol used is entirely open and can be verified, so after
        the game you can verify that neither the players nor the server cheated you. Support
        mails or tickets about rigged dice will lead to account suspensions.
    </li>
    <li><b>Cheating Accusation.</b> Public shaming of cheaters or public accusations of cheating
        are not tolerated and may lead to account suspensions.
    </li>
    <li><b>Spamming.</b> Some people are naturally more chatty than others. However,
        if you feel the need to post a link, use an excessive amount of smileys, exclamation marks, 
        or to say <i>Heeeeeelllllopoooooooo</i>, please talk to Martin. Do not post (unsollicited) 
        links, or advertise your social media, tournaments, etc.
    </li>
    <li><b>Disrespecting other players.</b>
        There is a fine line between being a good sport and sounding like a sore loser. It is ok
        to say "Good match", "Good luck", or similar at the start of end of the match. However,
        calling your opponent lucky, or swearing, or a simple "pfffff" after every good roll
        is considered disrespectful.
    </li>
</ol>

<h2>Our Rights</h2>
To operate, we need to be able to maintain control over what happens on our website. 
To summarise, we withhold the right to make decisions to protect the health and 
integrity of our systems. We’ll only use these powers when we absolutely must.

We can make changes to the OpenGammon Site and Services without letting you know 
in advance, or being liable for any loss, damage, or harm that arises as a result.

We have the ultimate say in who can use OpenGammon. We can cancel 
accounts, or decline to let users use OpenGammon.

We are not responsible for any loss, damage, or harm that may arise from 
any occasional downtime.

We reserve the right to contact the relevant authorities if a user is breaking 
the law, and share relevant data with the authorities, if harm or damage occurs 
either against our users or against the operation of our infrastructure.

We reserve the right to withhold financial awards or prizes from users we suspect 
of cheating or breaching other Fair Play and Community Guidelines, without needing 
to justify when, where, or how that user did so.

This list is non-exhaustive, and we reserve the right to add, edit, or remove 
entries from it at any time.

Services made available are on an as is and as available basis. We’re not 
responsible for whether this stuff actually works, though if it does that’s an 
added bonus to all of us.

OpenGammon makes no warranty or representation and disclaims all responsibility and 
liability for the completeness, availability, timeliness, accuracy and security 
of the content and services.

We disclaim all responsibility for any harm or loss of any data to your computer 
system, the deletion of or failure to store or transmit communications or content 
maintained by the services or whether the services will meet your requirements or 
be available on an uninterrupted, secure or error-free basis. OpenGammon cannot be 
held responsible for any loss, financial or otherwise, from third party sources, 
including misrepresentations, negligence or fraud.

No advice, information or written correspondence whether oral or written procured 
from OpenGammon entities or through OpenGammon services will create any warranty 
or representation not expressly made herein.

<h2>Advertisement</h2>
OpenGammon does not knowingly host adverts. OpenGammon is not responsible for 
third party adverts, widgets and apps hosted by the service providers on its social media.

<h2>Premium Accounts</h2>
As is explained on the <router-link :to="{name: 'features'}">Features page</router-link>
all features are open to use for any (registered) user of the site.

</div>
</template>

<style scoped>
    h1, :deep(h1){
        @apply text-4xl font-bold mt-6 mb-6
    }
    h2, :deep(h2){
        @apply text-2xl font-semibold my-4
    }
    h3, :deep(h3){
        @apply text-xl font-semibold
    }
    :deep(ol){
        @apply list-decimal list-inside my-4
    }
    :deep(ul){
        @apply list-disc list-inside my-4
    }
    :deep(blockquote){
        @apply px-4 my-4 border-s-4 border-main-3-color bg-main-2-color;
    }
    :deep(p){
        @apply mb-6
    }
    :deep(a){
        @apply underline hover:text-field-color
    }
    table, :deep(table){
        @apply w-full my-4
    }
    tbody tr, :deep(tbody tr){
        @apply hover:bg-main-2-color
    }
    :deep(th){
        @apply py-1 px-6 font-medium 
    }   
    :deep(td){
        @apply py-0.5 px-6 
    }   
    :deep(hr){
        @apply mt-4 mb-6 border-main-2-color
    }   
</style>
